import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PasswordResetForm from './PasswordResetForm';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onToggleMode: () => void;
  onClose: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode, onClose }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { login, signInWithGoogle, isLoading } = useAuth();
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError('');

    // Get reCAPTCHA token
    let captchaToken: string | undefined;
    if (executeRecaptcha) {
      try {
        captchaToken = await executeRecaptcha('login');
      } catch {
        setError(t('auth:login.captcha_failed'));
        return;
      }
    }

    const result = await login(data.email, data.password, captchaToken);
    if (result.success) {
      onClose();
    } else {
      setError(result.error || t('auth:login.invalid_email_password'));
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      const result = await signInWithGoogle();
      if (!result.success) {
        setError(result.error || t('auth:login.error_google_signin'));
      }
    } catch {
      setError('auth:login.error_google_signin');
    }
  };

  if (showPasswordReset) {
    return <PasswordResetForm onBackToLogin={() => setShowPasswordReset(false)} />;
  }

  return (
    <div className="space-y-5">
      {/* Error message */}
      {error && (
        <div className="bg-hig-error/10 border border-hig-error/30 text-hig-error px-4 py-3 rounded-hig text-sm">
          {error}
        </div>
      )}

      {/* Google Sign In */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 bg-hig-bg-tertiary border border-hig-border text-hig-text-primary py-3 px-4 rounded-hig font-medium hover:bg-hig-bg-elevated disabled:opacity-50 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {t('auth:login.continue_with_google')}
      </button>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-hig-divider"></div>
        <span className="text-sm text-hig-text-tertiary">{t('auth:login.or')}</span>
        <div className="flex-1 h-px bg-hig-divider"></div>
      </div>

      {/* Login form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="block text-sm text-hig-text-secondary mb-2">
            {t('auth:login.email')}
          </label>
          <input
            id="login-email"
            type="email"
            {...register('email')}
            className="input"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-hig-error">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="login-password" className="block text-sm text-hig-text-secondary mb-2">
            {t('auth:login.password')}
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              className="input pr-10"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-80 transition-opacity"
              style={{ color: 'rgba(15,23,42,0.35)' }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-hig-error">{errors.password.message}</p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowPasswordReset(true)}
            className="text-sm hover:underline"
            style={{ color: '#00897B' }}
          >
            {t('auth:login.forgot_password')}
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 rounded-lg font-bold text-sm transition-opacity hover:opacity-88 disabled:opacity-50"
          style={{ background: 'var(--accent)', color: '#0F172A', borderRadius: '8px' }}
        >
          {isLoading ? t('auth:login.signing_in') : t('auth:login.log_in')}
        </button>
      </form>

      {/* Toggle to signup */}
      <p className="text-center text-sm text-hig-text-secondary">
        {t('auth:login.no_account_yet')}{' '}
        <button
          type="button"
          onClick={onToggleMode}
          className="hover:underline"
          style={{ color: '#00897B' }}
        >
          {t('auth:login.create_account')}
        </button>
      </p>
    </div>
  );
};

export default LoginForm;